import type { FilletType } from '@puppycad/cad-core';

/**
 * 圆角项目列表可吸收的选择类型。
 *
 * 说明：
 * 1. 这里只描述“要圆角化的项目”本身的吸收规则；
 * 2. help point 之类的特殊入口不走这里；
 * 3. 统一在纯函数里建模，避免宿主与属性栏各自维护一份分叉规则。
 */
export type FilletProjectSelectionKind = 'edge' | 'face' | 'vertex';

/**
 * 判断当前圆角类型是否允许把指定选择类型吸收到“要圆角化的项目”中。
 *
 * @param type 圆角类型。
 * @param kind 选择类型。
 * @returns 是否允许吸收。
 */
export function isFilletProjectSelectionKindAllowed(
  type: FilletType,
  kind: FilletProjectSelectionKind,
): boolean {
  switch (type) {
    case 'constant':
      /**
       * 固定大小圆角的项目列表只允许边和面。
       * 固定大小圆角的主项目列表只允许边和面，顶点不能进入主项目列表。
       */
      return kind === 'edge' || kind === 'face';
    case 'variable':
      return kind === 'edge';
    case 'face':
      return kind === 'face';
    default:
      return false;
  }
}

/**
 * 判断固定大小圆角的“模式”是否应被锁定。
 *
 * @param items 当前项目列表。
 * @returns 是否应锁定为对称模式。
 */
export function isConstantFilletEdgeModeLocked(
  items: Array<{ kind: FilletProjectSelectionKind }>,
): boolean {
  /**
   * 只要项目里存在面，当前主链就必须锁回对称模式。
   * 这样可以保证 UI、定义模型和底层 solver 始终对齐。
   */
  return items.some((item) => item.kind === 'face');
}
